/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  CommunicationEntryPoint,
  DEFAULT_FILTER_STATE,
  EmailDialogConfig,
  emailDialogRoute,
  FilterNames,
  FormStateBuilder,
  resolveLeadsListFields,
  MeetingDialogConfig,
  meetingDialogRoute,
  OfficeAccessService,
  SmsDialogConfig,
  smsDialogRoute,
  StageAdvancementNotificationService,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  BuilderPermissionSet,
  CommunicationType,
  SystemRoleBit,
  LeadsDatesFilter,
  compareStageItemCodes,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  dictionaryItemFromDto,
  DictionaryService,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  globalDictionaries,
  UserContext,
} from '@gh/common-shared';
import {
  filterableControl,
  filterTracker,
  RouteDataService,
  SlicedDataSource,
} from '@gh/core-data';
import { DialogService, FormMode, newFormControl, NotificationService, WaitingService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridComponent,
  GridDefinitionFactory,
  GridLayout,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';

import { LeadListBusinessService } from '../../resources/lead-list-business.service';
import { BulkEditLeadsDialogComponent } from '../bulk-edit-leads-dialog/bulk-edit-leads-dialog.component';
import { OfficeBulkReassignLeadsDialogComponent } from '../office-bulk-reassign-leads-dialog/office-bulk-reassign-leads-dialog.component';
import {
  LEADS_LIST_TRANSIENT_QUERY_PARAMS,
  markLeadsListPageReloadHandled,
  peekLeadsListPageReload,
  resolveLeadsListSalesPersonId,
  rewriteFilterQueryWithoutHiddenLeads,
} from '@gh/builder-shared';
import { LeadListItem } from '../../resources/lead.model';
import {
  salesManagementReportFilterDialogRoute,
} from './sales-management-report-filter-dialog/sales-management-report-filter-dialog.route';
import {
  isSalespersonSelectItemInactive,
  LeadsListFilter,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import { merge$ } from '@gh/rx';
import { filter$, map$, tap$ } from '@gh/rx/operators';
import { currentDate, sortByNumberField, sortByStringField } from '@gh/core-util';
import { combineLatest, Subscription } from 'rxjs';
import { ROW_WARNING_CLASS } from '@gh/common-internal-shared';
import { omit } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-lead-list-page',
  templateUrl: 'lead-list.component.html',
  styleUrls: ['lead-list.component.scss'],
})
export class LeadListComponent implements OnInit, OnDestroy {
  dataSource: SlicedDataSource<LeadListItem>;
  gridDefinition: LayoutBasedGridDefinition;
  private leadsGridLayout: GridLayout;
  private stageChangeSubscription?: Subscription;
  private lastListFieldsKey = '';
  form: FilterableFormState<LeadsListFilter>;
  name = filterableControl<string>(newFormControl(''));
  cursor = newGridPaginationCursor();
  subscription$ = new Subscription();
  reassignRestricted = false;
  notProceedingReasons: any[];
  salesPersonsList: any[];
  stages: any[];
  salesFolderStatuses: any[];
  priorities: any[];
  referredByList: any[];
  willNotComplete = 3;
  selectedLeads: Set<number> = new Set<number>();
  currentItems: LeadListItem[] = [];
  @ViewChild(GridComponent) grid: GridComponent;

  /** Date filter type options for Office leads (like Contact Manager Dates). */
  leadsDateTypeOptions: { id: LeadsDatesFilter; nameKey: string }[] = [
    { id: LeadsDatesFilter.CreateDate, nameKey: 'lbl_leads_date_create' },
    { id: LeadsDatesFilter.LastContactDate, nameKey: 'lbl_leads_date_last_contact' },
  ];

  get isSalespersonListExist(): boolean {
    return !!this.salesPersonsList.length;
  }

  get isStageListExist(): boolean {
    return !!this.stages && !!this.stages.length;
  }

  get hasEditRules(): boolean {
    return this.builderContext.hasSystemRole(SystemRoleBit.Master) || this.builderContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.builderContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);
  }

  /** Salesperson users (without a Corporate, Master or Franchisee role) may not reassign leads. */
  get canBulkReassignLeads(): boolean {
    // Multi-office: additionally hidden for a Salesperson working in a membership (child) office
    if (this.reassignRestricted) {
      return false;
    }
    const ctx = this.builderContext;
    if (ctx.hasSystemRole(SystemRoleBit.Master) || ctx.hasSystemRole(SystemRoleBit.Corporate)) {
      return true;
    }
    if (ctx.hasSystemRole(SystemRoleBit.SalesPerson) && !ctx.hasSystemRole(SystemRoleBit.Franchisee)) {
      return false;
    }
    return ctx.hasPermission(BuilderPermission.Sales.Leads.REASSIGN);
  }

  get hasEventLogViewPermission(): boolean {
    return this.builderContext.hasSystemRole(SystemRoleBit.Master) || this.builderContext.hasSystemRole(SystemRoleBit.Corporate) ||
      this.builderContext.hasPermission(BuilderPermission.EventLog.VIEW);
  }

  get getSalesPersonName(): string {
    if (this.form.get('salesPersonId')?.value) {
      const targetSalesPerson = this.salesPersonsList.find((person) => person.id === this.form.get('salesPersonId')?.value);
      return targetSalesPerson ? targetSalesPerson.name : '';
    }
    return '';
  }

  constructor(private route: ActivatedRoute,
              private angRouter: Router,
              private routeData: RouteDataService,
              private dictionaryService: DictionaryService,
              private businessService: LeadListBusinessService,
              private router: AppRouter,
              private navigationService: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private userContext: UserContext,
              private builderContext: BuilderUserContext,
              private dialogService: DialogService,
              private formStateBuilder: FormStateBuilder,
              private notificationService: NotificationService,
              private waiting: WaitingService,
              private cdr: ChangeDetectorRef,
              private officeAccessService: OfficeAccessService,
              private stageAdvancement: StageAdvancementNotificationService,
              ) {
    const date = currentDate();
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        ref: {
          minWidth: 110,
          valueGetter: (field) => {
            const id = field.data?.leadId || field.data?.ref || field.data?.contactId;
            return id ? (String(id).startsWith('L-') ? id : `L-${id}`) : '';
          },
          comparator: (a, b, nodeA, nodeB) => (Number(nodeA.data?.leadId || nodeA.data?.contactId) || 0) - (Number(nodeB.data?.leadId || nodeB.data?.contactId) || 0),
        },
        lastContactDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        createdDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        nextActivity: {
          ...buildDateTimeColumn(DateTimeTypes.DateTime),
          cellClass: ({data}: { data: LeadListItem }) =>  data.nextActivity && data.nextActivity <= date ? 'next-activity-warning' : '',
        },
        meetingSfStatusId: {
          minWidth: 100,
          cellClassRules: {
            'active-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 1,
            'finalized-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 2,
            'deleted-status': ({ data }: { data: LeadListItem }) => Number(data.meetingSfStatusId) === 3,
          },
          valueGetter: (field) => {
            let result = this.salesFolderStatuses.find(
              (status) => parseInt(status.itemCode) === field.data.meetingSfStatusId)?.localization;
            if (parseInt(field.data.meetingSfStatusId) === this.willNotComplete && field.data.notProceedingReason) {
              result += ` (${ this.notProceedingReasons.find((reason) => reason.id.toString() === field.data.notProceedingReason?.toString())?.name})`;
            }
            return result;
          },
        },
        meetingSfPriorityId: {
          valueGetter: (field) =>
            this.priorities.find((priority) => parseInt(priority.itemCode) === field.data.meetingSfPriorityId)?.localization,
        },
        meetingSfStageId: {
          valueGetter: (field) =>
            this.stages.find((stage) => parseInt(stage.itemCode) === field.data.meetingSfStageId)?.localization,
        },
      },
      {
        defaultColDef: {
          comparator: () => 0,
        },
        rowClassRules: {
          [ROW_WARNING_CLASS]: ({ data }: { data: LeadListItem }) => !data.activeSalesFolderId,
        },
        getRowId: (params) => String(params.data.contactId)
      });
  }

  ngOnInit(): void {
    this.subscription$.add(this.officeAccessService.isReassignRestricted(this.builderContext).subscribe((restricted) => {
      this.reassignRestricted = restricted;
      this.cdr.markForCheck();
    }));
    const {
      filter: initFilter,
      gridLayout,
      salesPersonsList,
      salesFolderStatuses,
      salesPersonId,
    } = getRouteInitData(this.route);
    combineLatest([
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.stage),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.priority),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.referredBy),
      this.dictionaryService.getDictionaryFromGlobal(globalDictionaries.notProceedingReasons).pipe(
        map$((res) => res.map(dictionaryItemFromDto))),
                  ]).subscribe(([stagesDict, priorities, referred, notProceedingReasonsList]) => {
      this.stages = [...stagesDict].sort((a, b) => compareStageItemCodes(a.itemCode, b.itemCode));
      this.priorities = sortByStringField(priorities, 'localization');
      this.referredByList =  sortByNumberField(referred, 'itemCode');
      this.notProceedingReasons = notProceedingReasonsList;
    });
    this.leadsGridLayout = gridLayout;
    this.gridDefinition.bind(gridLayout);
    this.salesPersonsList = salesPersonsList;
    const pageReload = peekLeadsListPageReload();
    const resultSalesPersonId = resolveLeadsListSalesPersonId(
      salesPersonId,
      pageReload ? void 0 : initFilter.salesPersonId,
      salesPersonsList,
      this.builderContext,
    );
    const defaultFilter: LeadsListFilter = {
      ...DEFAULT_FILTER_STATE[FilterNames.leads],
      salesPersonId: resultSalesPersonId,
    };
    this.salesFolderStatuses = salesFolderStatuses;
    this.form = filterableForm(this.formStateBuilder.buildPlain({
                                                                  value: pageReload ? defaultFilter : {
                                                                    ...initFilter,
                                                                    salesPersonId: resultSalesPersonId,
                                                                    showSuppressed: false,
                                                                  },
                                                                  children: {
                                                                    searchString: void 0,
                                                                    showInactive: void 0,
                                                                    showActive: void 0,
                                                                    showOverdue: void 0,
                                                                    showCompleted: void 0,
                                                                    salesPersonId: void 0,
                                                                    stage: void 0,
                                                                    priority: void 0,
                                                                    referredBy: void 0,
                                                                    fromDate: void 0,
                                                                    toDate: void 0,
                                                                    dateType: void 0,
                                                                    showSuppressed: void 0,
                                                                  },
                                                                }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form).withDefault(defaultFilter))
      .withEffects(cursorTracker(this.cursor));
    const onFilterChange = ([filter]: [LeadsListFilter]) => {
      // Prevent API calls when date filter is incomplete (Type selected without From/To, or vice versa)
      const hasDateType = !!filter.dateType;
      const hasFromDate = !!filter.fromDate;
      const hasToDate = !!filter.toDate;
      const hasBothDates = hasFromDate && hasToDate;
      const hasAnyDate = hasFromDate || hasToDate;

      const isDateFilterComplete = (hasDateType && hasBothDates) || (!hasDateType && !hasAnyDate);

      if (!isDateFilterComplete) {
        return;
      }

      const fixedFilter = omit(filter, 'stage', 'priority', 'salesPersonId', 'referredBy') as LeadsListFilter;
      if (filter.stage) {
        fixedFilter.stage = filter.stage;
      }
      if (filter.priority) {
        fixedFilter.priority = filter.priority;
      }
      if (filter.salesPersonId) {
        fixedFilter.salesPersonId = filter.salesPersonId;
      }
      if (filter.referredBy) {
        fixedFilter.referredBy = filter.referredBy;
      }
      if (filter.dateType) {
        fixedFilter.dateType = filter.dateType;
      }
      if (filter.fromDate != null) {
        fixedFilter.fromDate = filter.fromDate;
      }
      if (filter.toDate != null) {
        fixedFilter.toDate = filter.toDate;
      }
      this.dataSource = {
        select: (query) => this.businessService.findAllAsDataSource(fixedFilter, this.leadsGridLayout).select(query).pipe(
          tap$((res) => {
            this.currentItems = res.data;
            this.selectedLeads.clear();
            this.cdr.markForCheck();
          }),
        ),
      };
    };

    if (pageReload) {
      markLeadsListPageReloadHandled();
      this.form.setFilter({ ...defaultFilter, showSuppressed: false });
      this.clearTransientQueryParams();
      tracker.listen(this.route);
      tracker.filterChanges.subscribe(onFilterChange);
    } else {
      this.clearHiddenLeadsFromFilterQuery().then(() => {
        this.form.get('showSuppressed')?.setValue(false);
        tracker.listen(this.route);
        tracker.filterChanges.subscribe(onFilterChange);
      });
    }
    // Reload after an automatic stage change and after its undo, as the board and lead details do.
    this.stageChangeSubscription = merge$(
      this.stageAdvancement.stageAdvanced$,
      this.stageAdvancement.stageUndone$,
    ).subscribe(() => {
      this.form.emitFilter();
    });
  }

  ngOnDestroy(): void {
    if (this.stageChangeSubscription) {
      this.stageChangeSubscription.unsubscribe();
    }
  }

  private clearHiddenLeadsFromFilterQuery(): Promise<boolean> {
    const rewritten = rewriteFilterQueryWithoutHiddenLeads(this.route.snapshot.queryParams['filter']);
    if (!rewritten) {
      return Promise.resolve(true);
    }
    return this.angRouter.navigate([], {
      relativeTo: this.route,
      queryParams: { filter: rewritten },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private clearTransientQueryParams(): void {
    const cleared: Params = {};
    LEADS_LIST_TRANSIENT_QUERY_PARAMS.forEach((name) => {
      cleared[name] = null;
    });
    this.angRouter.navigate([], {
      relativeTo: this.route,
      queryParams: cleared,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** True when any date filter (dateType, fromDate, toDate) has a value – show Reset button. */
  get isDatesResetVisible(): boolean {
    if (!this.form) {
      return false;
    }
    const dateType = this.form.get('dateType')?.value;
    const fromDate = this.form.get('fromDate')?.value;
    const toDate = this.form.get('toDate')?.value;
    return !!dateType || fromDate != null || toDate != null;
  }

  /** Reset date type and From/To (like Contact Manager Dates reset). */
  onResetDates(): void {
    this.form.get('dateType')?.setValue(null);
    this.form.get('fromDate')?.setValue(null);
    this.form.get('toDate')?.setValue(null);
    this.form.emitFilter();
  }

  onDateFilterChange(): void {
    const dateType = this.form.get('dateType')?.value;
    const fromDate = this.form.get('fromDate')?.value;
    const toDate = this.form.get('toDate')?.value;

    const hasDateType = !!dateType;
    const hasFromDate = fromDate != null;
    const hasToDate = toDate != null;

    if ((hasDateType && hasFromDate && hasToDate) || (!hasDateType && !hasFromDate && !hasToDate)) {
      this.form.emitFilter();
    }
  }

  onCreate(): void {
    this.router.navigateToChild(['/sales/leads/new'], {
      parentTitle: 'lbl_leads',
    });
  }

  onEdit(lead: LeadListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.EDIT_VIEW)) {
      this.router.navigateToChild(['/sales/leads', lead.contactId], {
        parentTitle: 'lbl_leads',
      });
    }
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onOpenSalesManagementReport(): void {
    this.dialogService.openWithInput(salesManagementReportFilterDialogRoute, {});
  }

  onLeadBoardClick(): void {
    this.router.navigateToChild(['/sales/leads-board'], {
      parentTitle: 'lbl_leads',
    });
  }

  goToAel(lead?: LeadListItem): void {
    this.navigationService.goToAel({
      label: 'lbl_leads',
      entityType: 'LEAD',
      entityId: lead ? lead.contactId : undefined,
    });
  }

  isSalespersonInactive(salesperson: SalespersonSelectItem): boolean {
    return isSalespersonSelectItemInactive(salesperson);
  }

  onFilterChange(): void {
    this.form.emitFilter();
  }

  onGridLayoutChange(layout: GridLayout): void {
    this.leadsGridLayout = layout;
    const fieldsKey = (resolveLeadsListFields(layout, this.form?.currentFilter || {}, layout.sort?.field) || [])
      .sort()
      .join('|');
    if (fieldsKey === this.lastListFieldsKey) {
      return;
    }
    this.lastListFieldsKey = fieldsKey;
    this.form.emitFilter();
  }

  onSendSms(contact: LeadListItem): void {
    const phoneList = contact.contactPhone ? contact.contactPhone.split(';') : [''];
    let primaryPhone = '';
    if (!!phoneList[0] && phoneList[0].trim() !== '-') {
      primaryPhone = phoneList[0];
    } else if (phoneList[1]) {
      primaryPhone = phoneList[1];
    }
    this.dialogService.openWithInput(smsDialogRoute, <SmsDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId: contact.contactId,
      sms: {
        cellPhone: primaryPhone,
        subject: '',
        message: '',
        communicationType: CommunicationType.OutgoingSms,
        attachments: [],
        contactId: contact.contactId,
        isMarkedAsRead: true,
      },
    })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {  });
  }

  onSendEmail(contact: LeadListItem): void {
    const emailList = contact.contactEmail ? contact.contactEmail.split(';') : [''];
    let primaryEmail = '';
    if (!!emailList[0] && emailList[0].trim() !== '-') {
      primaryEmail = emailList[0];
    } else if (emailList[1]) {
      primaryEmail = emailList[1];
    }
    this.dialogService.openWithInput(emailDialogRoute, <EmailDialogConfig>{
      entryPoint: CommunicationEntryPoint.Lead,
      contactId: contact.contactId,
      gFolderId: contact.customerGoogleFolderId,
      email: {
        email: primaryEmail,
        subject: '',
        message: '',
        communicationType: CommunicationType.Email,
        attachments: [],
        contactId: contact.contactId,
        isMarkedAsRead: true,
      },
    })
      .afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {  });
  }

  onAddMeeting(contact: LeadListItem): void {
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.LeadList,
      salesFolderId: contact.activeSalesFolderId,
      mode: FormMode.CreateMode,
      helpId: 'sales/meetings',
      leadId: contact.contactId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe(() => {});
  }

  onShowCompletedChange(value: any): void {
    if (value.checked) {
      this.form.get('showInactive').setValue(false);
    }
    this.form.emitFilter();
  }

  onShowInactiveChange(value: any): void {
    if (value.checked) {
      this.form.get('showCompleted').setValue(false);
    }
    this.form.emitFilter();
  }

  isSelectionModeActive(): boolean {
    return this.selectedLeads.size > 0;
  }

  onToggleSelection(lead: LeadListItem): void {
    const cid = lead.contactId as any as number;
    if (this.selectedLeads.has(cid)) {
      this.selectedLeads.delete(cid);
    } else {
      if (this.selectedLeads.size >= 200) {
        this.notificationService.warning('msg_max_200_leads_selection_limit');
        return;
      }
      this.selectedLeads.add(cid);
    }
    this.cdr.markForCheck();
  }

  onToggleAllSelection(checked: boolean): void {
    if (checked) {
      this.currentItems.forEach((lead: LeadListItem) => {
        if (this.selectedLeads.size < 200) {
          this.selectedLeads.add(lead.contactId as any as number);
        }
      });
    } else {
      this.selectedLeads.clear();
    }
    this.cdr.markForCheck();
  }

  isLeadSelected(lead: LeadListItem): boolean {
    return this.selectedLeads.has(lead.contactId as any as number);
  }

  get allOnPageSelected(): boolean {
    return this.currentItems.length > 0
      && this.currentItems.every((lead: LeadListItem) => this.selectedLeads.has(lead.contactId as any as number));
  }

  get isSelectedPartial(): boolean {
    return this.selectedLeads.size > 0 && !this.allOnPageSelected;
  }

  get selectedLeadsCountLabel(): string {
    return this.selectedLeads.size === 1 ? 'lbl_lead_selected' : 'lbl_leads_selected';
  }

  onBulkEditLeads(): void {
    if (this.selectedLeads.size === 0) {
      return;
    }

    const selectedList = this.currentItems.filter((lead: LeadListItem) =>
      this.selectedLeads.has(lead.contactId as any as number),
    );
    const COMPLETED_STATUS_ID = 2;
    if (selectedList.some((lead) => lead.meetingSfStatusId === COMPLETED_STATUS_ID)) {
      this.notificationService.warning('msg_bulk_edit_completed_leads_error');
      return;
    }

    this.dialogService.open(BulkEditLeadsDialogComponent, {
      data: {
        contactIds: Array.from(this.selectedLeads).map((id) => id.toString()),
        reasons: this.notProceedingReasons,
        stages: this.stages,
        priorities: this.priorities,
      },
      width: '600px',
      disableClose: true,
    }).afterClosed().subscribe((success) => {
      if (success) {
        this.selectedLeads.clear();
        this.cdr.markForCheck();
        this.onSearch();
      }
    });
  }

  onBulkReassignLeads(): void {
    if (this.selectedLeads.size === 0 || !this.canBulkReassignLeads) {
      return;
    }

    const selectedList = this.currentItems.filter((lead: LeadListItem) =>
      this.selectedLeads.has(lead.contactId as any as number),
    );
    const COMPLETED_STATUS_ID = 2;
    if (selectedList.some((lead) => lead.meetingSfStatusId === COMPLETED_STATUS_ID)) {
      this.notificationService.warning('msg_bulk_reassign_completed_leads_error');
      return;
    }

    const officeId = this.builderContext.office?.id;
    if (!officeId) {
      return;
    }

    const sourceSalespersonIds = [...new Set(selectedList.map((lead) => String(lead.salespersonId)))];

    this.dialogService.open(OfficeBulkReassignLeadsDialogComponent, {
      data: {
        leadIds: selectedList.map((lead) => String(lead.activeSalesFolderId)),
        officeId: String(officeId),
        sourceSalespersonIds,
      },
      width: '520px',
      disableClose: true,
    }).afterClosed().subscribe((success) => {
      if (success) {
        this.selectedLeads.clear();
        this.cdr.markForCheck();
        this.onSearch();
      }
    });
  }
}
